import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock } from 'lucide-react'
import { useMe } from '@/features/auth/hooks'

const REMINDER_MS = 15 * 60_000

/**
 * AF-135 — surfaces 15 minutes before the user's shift ends. Reads the
 * user's shift from the /me endpoint (no extra round-trip; /me is loaded
 * app-wide for permission checks).
 *
 * Renders nothing while a shift is set far in the future, when the user
 * has no shift, or after their shift has ended (the post-end window is
 * the responsibility of the EndShift page itself).
 */
export function ShiftEndBanner() {
  const { data: me } = useMe()
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  if (!me?.shift) return null
  // ends_at is optional on the new MeShift contract — open shifts
  // (started but not yet closed) carry no scheduled end. Without a
  // valid end time there's nothing to count down to, so render
  // nothing rather than NaN. parseFloat-style guard catches both
  // missing strings and unparseable values like "" or "TBD".
  const endsAtRaw = me.shift.ends_at
  if (!endsAtRaw) return null
  const endsAt = new Date(endsAtRaw).getTime()
  if (Number.isNaN(endsAt)) return null
  const msLeft = endsAt - now
  if (msLeft <= 0 || msLeft > REMINDER_MS) return null

  const minutes = Math.max(1, Math.ceil(msLeft / 60_000))

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-status-pending/30 bg-status-pending/10 px-4 py-2 text-sm"
    >
      <span className="flex items-center gap-2 text-status-pending">
        <Clock size={14} aria-hidden /> Your shift ends in{' '}
        <strong>{minutes} min</strong>. Wrap up your handover so the
        incoming analyst isn't starting blind.
      </span>
      <Link
        to="/handovers/end-shift"
        className="rounded border border-status-pending/40 bg-card px-2 py-1 text-xs font-medium text-status-pending hover:bg-status-pending/5"
      >
        Open handover form
      </Link>
    </div>
  )
}
